import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { 
  Building, 
  UserPlus, 
  Search, 
  Plus, 
  Mail,
  PlusCircle,
  BarChart3,
  Users,
  FileSpreadsheet
} from 'lucide-react';
import CompanyModal from './CompanyModal';
import CompanyList from './CompanyList';

import CustomEmailSender from './CustomEmailSender';
import BulkSupporterContributions from './BulkSupporterContributions';

interface Company {
  id: number;
  title: { rendered: string };
  meta: {
    _bms_nip: string;
    _bms_city: string;
    _bms_postal_code: string;
    _bms_street: string;
    _bms_number: string;
    _bms_company_email: string;
    _bms_member_type: string;
    _account_status: string;
    _assigned_users: string[];
    _bms_recipient_name: string;
    _bms_recipient_city: string;
    _bms_recipient_postal_code: string;
    _bms_recipient_street: string;
    _bms_recipient_number: string;
    _bms_recipient_nip: string;
    _bms_recipient_email: string;
  };
  resolution_file?: {
    url: string;
    name: string;
  };
}

const tabs = [
  { id: 'companies', label: __('Company Management'), icon: Building },
  { id: 'custom-email', label: __('Send Custom Email'), icon: Mail },
];

const CompanyManagement = () => {
  const [activeTab, setActiveTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Initial data fetch
useEffect(() => {
    if (activeTab === 'companies') {
      fetchCompanies();
    }
  }, [activeTab]);

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedCompany(null);
  };

  // Fetch companies data
  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/companies`, {
        headers: {
          'X-WP-Nonce': boardMemberData.nonce
        }
      });
      const data = await response.json();
      setCompanies(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch companies');
      setLoading(false);
    }
  };

  // Toggle company block status
  const handleToggleBlock = async (companyId) => {
    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/companies/${companyId}/toggle-block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to toggle company status');
      }

      // Preserve pagination while updating data
      await fetchCompanies();
    } catch (err) {
      setError(err.message || 'Failed to toggle company status');
    }
  };

  // Delete company
  const handleDelete = async (companyId) => {
    if (!window.confirm(__('Are you sure you want to delete this company?'))) {
      return;
    }

    try {
      const response = await fetch(`${boardMemberData.restURL}wp/v2/company/${companyId}`, {
        method: 'DELETE',
        headers: {
          'X-WP-Nonce': boardMemberData.nonce
        }
      });

      if (response.ok) {
        await fetchCompanies();
      }
    } catch (err) {
      setError('Failed to delete company');
    }
  };

  // Handle editing a company
  const handleEdit = (company) => {
    setSelectedCompany(company);
    setShowModal(true);
  };

  // Handle saving company data
  const handleSave = async (data) => {
    try {
      await fetchCompanies(); // Refresh data while maintaining pagination
      handleModalClose();
    } catch (error) {
      console.error('Error saving company:', error);
      setError('Failed to save company data');
    }
  };

  if (!boardMemberData.isAdmin) {
    return null;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'companies':
        return (
          <div className="space-y-6">
            {/* Companies Tab Content */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder={__('Search companies...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={() => {
              setSelectedCompany(null);
              setShowModal(true);
            }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                {__('Add Company')}
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4">{__('Loading...')}</div>
            ) : (
              <CompanyList
                companies={companies}
                onToggleBlock={handleToggleBlock}
                onDelete={handleDelete}
                onEdit={handleEdit}
                searchTerm={searchTerm}
                pagination={pagination}
                setPagination={setPagination}
              />
            )}

            {showModal && (
        <CompanyModal
          company={selectedCompany}
          onClose={handleModalClose}
          onSave={handleSave}
        />
      )}
          </div>
        );

      case 'custom-email':
        return <CustomEmailSender />;

      default:
        return <div>{__('Tab content not found')}</div>;
    }
  };

  // return (
  //   <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
  //     <div className="sm:flex sm:items-center">
  //       <div className="sm:flex-auto">
  //         <h1 className="text-xl font-semibold text-gray-900">{__('Company Management')}</h1>
  //       </div>
  //       <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
  //         <button
  //           onClick={() => {
  //             setSelectedCompany(null);
  //             setShowModal(true);
  //           }}
  //           className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
  //         >
  //           <PlusCircle className="h-5 w-5 mr-2" />
  //           {__('Add Company')}
  //         </button>
  //       </div>
  //     </div>

  //     <div className="mt-4">
  //       <div className="relative">
  //         <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
  //           <Search className="h-5 w-5 text-gray-400" />
  //         </div>
  //         <input
  //           type="text"
  //           className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
  //           placeholder={__('Search companies...')}
  //           value={searchTerm}
  //           onChange={(e) => setSearchTerm(e.target.value)}
  //         />
  //       </div>
  //     </div>

  //     {error && (
  //       <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
  //         {error}
  //       </div>
  //     )}

  //     {loading ? (
  //       <div className="mt-4 flex justify-center">
  //         <div className="loader">{__('Loading...')}</div>
  //       </div>
  //     ) : (
  //       <div className="mt-8">
  //         <CompanyList
  //           companies={companies}
  //           onToggleBlock={handleToggleBlock}
  //           onDelete={handleDelete}
  //           onEdit={handleEdit}
  //           searchTerm={searchTerm}
  //           pagination={pagination}
  //           setPagination={setPagination}
  //         />
  //       </div>
  //     )}

  //     {showModal && (
  //       <CompanyModal
  //         company={selectedCompany}
  //         onClose={handleModalClose}
  //         onSave={handleSave}
  //       />
  //     )}
  //   </div>
  // );
  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon
                    className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${activeTab === tab.id
                        ? 'text-blue-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                      }
                    `}
                  />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default CompanyManagement;